import { ArrowRight, ExternalLink, TriangleAlert } from "lucide-react";

import { ButtonLink } from "@/components/button-link";
import type { Challenge } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Reminder that a graded challenge needs a second, official submission.
 *
 * Uploading here scores a run and places it on the leaderboard; the grade comes
 * from a Google Form. Students reliably assume the upload was the submission, so
 * this appears at three points — browsing the challenge, before uploading, and
 * most importantly straight after a successful upload, when the run feels done.
 *
 * Renders nothing unless the challenge is a competition with a form link, or one that
 * collects its report on this site, so call sites do not need to check.
 */
export function CompetitionNotice({
  challenge,
  variant = "banner",
  className,
}: {
  challenge: Challenge;
  variant?: "banner" | "next-step";
  className?: string;
}) {
  if (challenge.type !== "competition") return null;

  // The report is handed in on this site: point at its card on the same page.
  if (challenge.collects_report && variant === "banner") {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-500/40 bg-amber-500/8 px-4 py-3 text-sm",
          className,
        )}
      >
        <TriangleAlert
          className="size-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <p className="min-w-0 flex-1">
          Graded challenge. After your runs, your team also hands in a report on this page:
          difficulty, slides as a PDF, and your agent&apos;s code.
        </p>
        <ButtonLink href="#report" size="sm" variant="outline">
          Hand in the report
          <ArrowRight className="size-3 opacity-60" aria-hidden />
        </ButtonLink>
      </div>
    );
  }

  if (!challenge.final_submission_link) return null;

  const link = challenge.final_submission_link;

  if (variant === "next-step") {
    return (
      <div
        className={cn(
          "rounded-lg border border-amber-500/40 bg-amber-500/8 p-4",
          className,
        )}
      >
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-amber-500/20 text-xs font-bold text-amber-700 dark:text-amber-300"
            aria-hidden
          >
            !
          </span>
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="font-medium">You are not done yet</p>
              <p className="text-muted-foreground mt-0.5 text-sm">
                This upload is scored and on the leaderboard, but it is{" "}
                <strong className="text-foreground">not your submission</strong>.
                Submit the same <code className="font-mono">.npy</code>, your
                report, and your team through the official form.
              </p>
            </div>
            <ButtonLink href={link} size="sm">
              Open submission form
              <ArrowRight className="size-4" aria-hidden />
            </ButtonLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-500/40 bg-amber-500/8 px-4 py-3 text-sm",
        className,
      )}
    >
      <TriangleAlert
        className="size-4 shrink-0 text-amber-600 dark:text-amber-400"
        aria-hidden
      />
      <p className="min-w-0 flex-1">
        Graded challenge — runs here are scored, but your submission must go through
        the official form.
      </p>
      <ButtonLink href={link} size="sm" variant="outline">
        Submission form
        <ExternalLink className="size-3 opacity-60" aria-hidden />
      </ButtonLink>
    </div>
  );
}
